import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Boxes, X, Trash2, ChevronRight } from "lucide-react";

const EMPTY_FORM = { name: "", icon: "Boxes" };

export default function CustomModules() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/custom-modules");
      setItems(data || []);
    } catch {
      toast.error("Failed to load custom modules");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/custom-modules", form);
      toast.success("Module created");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create module");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete module "${m.name}"? This also deletes its fields and every record in it.`)) return;
    try {
      await api.delete(`/custom-modules/${m.module_id}`);
      toast.success("Module deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete module");
    }
  };

  return (
    <DarkPage><div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Boxes className="w-5 h-5 text-[#7d8590]" /> Custom Modules</h1>
          <p className="text-sm text-[#7d8590] mt-1">Define your own object types — Suppliers, Assets, Vendors — with their own fields and records, no code required.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Module
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-[#7d8590] text-sm">No custom modules yet. Create one to start building your own data model.</div>
      ) : (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#21262d]">
              {["Module", "Records", ""].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map(m => (
                <tr key={m.module_id} className="border-b border-[#21262d] last:border-0 group">
                  <td className="px-4 py-3">
                    <Link to={`/custom-modules/${m.module_id}`} className="text-white hover:text-[#00F5D4] flex items-center gap-1.5">
                      <Boxes className="w-3.5 h-3.5 text-[#7d8590]" /> {m.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-[#7d8590]">{m.record_count}</td>
                  <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                    <button onClick={() => remove(m)} className="opacity-0 group-hover:opacity-100 p-1.5 text-[#7d8590] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    <Link to={`/custom-modules/${m.module_id}`} className="p-1.5 text-[#7d8590] hover:text-white"><ChevronRight className="w-3.5 h-3.5" /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Module</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Suppliers"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create module"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
